import React, { useState } from 'react';
import { View, UserSettings } from '../types';
import { getDashboardGreeting, getDashboardSubtitle } from '../utils/dashboardGreetings';
import { 
    DocumentTextIcon, 
    QuestionMarkCircleIcon, 
    BoltIcon, 
    PresentationChartBarIcon, 
    ClipboardDocumentListIcon,
    PuzzlePieceIcon,
    Square2StackIcon,
    ChevronRightIcon,
    UserGroupIcon,
    ChartBarIcon,
    SparklesIcon,
    PlayIcon,
    AcademicCapIcon,
    FlagIcon,
    WalletIcon,
} from './common/Icons';
import HelpOnboarding, { HelpStep } from './common/HelpOnboarding';
import LoadingDataIndicator from './common/LoadingDataIndicator';
import { FEATURE_FLAGS } from '../config';

const teacherHelpSteps: HelpStep[] = [
    {
        title: 'Công cụ soạn thảo AI',
        description: 'Sử dụng AI để tạo giáo án 5E, bài kiểm tra, trò chơi tương tác và video bài giảng chỉ trong vài giây.',
        icon: <SparklesIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Quản lý Lớp học',
        description: 'Tạo lớp mới, quản lý sĩ số học sinh và mời phụ huynh tham gia qua mã mời hoặc link chia sẻ.',
        icon: <UserGroupIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Giao & Chấm bài tập',
        description: 'Giao bài tập đa dạng từ thư viện hoặc tự tạo. Chấm điểm trực tiếp và gửi nhận xét tức thì cho học sinh và phụ huynh.',
        icon: <ClipboardDocumentListIcon className="h-12 w-12 text-teal-600" />
    },
    {
        title: 'Báo cáo thông minh',
        description: 'Xem báo cáo chi tiết về điểm số, tỉ lệ chuyên cần và tiến độ học tập của cả lớp hoặc từng học sinh.',
        icon: <ChartBarIcon className="h-12 w-12 text-teal-600" />
    }
];

interface TeacherDashboardProps {
    onNavigate: (view: View, payload?: object) => void;
    user: UserSettings;
    isLoading?: boolean;
    onUpdateSettings?: React.Dispatch<React.SetStateAction<UserSettings | null>>;
}

const ToolCard: React.FC<{ 
    title: string; 
    description?: string;
    icon: React.ReactNode; 
    onClick: () => void;
    accent: string;
}> = ({ title, description, icon, onClick, accent }) => (
    <button 
        onClick={onClick} 
        className={`w-full text-left p-4 sm:p-5 rounded-2xl border border-slate-200/80 bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-400 overflow-hidden relative`}
    >
        <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-10 ${accent}`} />
        <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
                <div className={`flex-shrink-0 h-12 w-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${accent}`}>
                    {icon}
                </div>
                <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-800 group-hover:text-teal-700 transition-colors">{title}</h3>
                    {description && (
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{description}</p>
                    )}
                </div>
            </div>
            <ChevronRightIcon className="h-5 w-5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
        </div>
    </button>
);

const TOOL_GROUPS = [
    {
        label: 'Quản lý & Thư viện',
        icon: <UserGroupIcon className="h-5 w-5" />,
        tools: [
            { view: View.ClassManagement, title: 'Quản lý Lớp học', description: 'Quản lý học sinh, giao bài và theo dõi tiến độ', icon: <UserGroupIcon className="h-6 w-6 text-white" />, accent: 'bg-teal-500' },
            { view: View.DocumentLibrary, title: 'Thư viện Tài liệu', description: 'Lưu trữ giáo án, slide, trò chơi và tài nguyên', icon: <Square2StackIcon className="h-6 w-6 text-white" />, accent: 'bg-slate-600' },
        ],
    },
    {
        label: 'Tạo nội dung giảng dạy',
        icon: <DocumentTextIcon className="h-5 w-5" />,
        tools: [
            { view: View.LessonPlanCreator, title: 'Tạo Giáo án', description: 'Soạn giáo án theo mô hình 5E, GDPT 2018', icon: <DocumentTextIcon className="h-6 w-6 text-white" />, accent: 'bg-blue-500' },
            { view: View.SlideCreator, title: 'Tạo Trình chiếu', description: 'Biến tài liệu thành slide sinh động', icon: <PresentationChartBarIcon className="h-6 w-6 text-white" />, accent: 'bg-emerald-500' },
            { view: View.QuizCreator, title: 'Tạo Bài kiểm tra', description: 'Thiết kế trắc nghiệm, tự luận nhanh chóng', icon: <QuestionMarkCircleIcon className="h-6 w-6 text-white" />, accent: 'bg-violet-500' },
            { view: View.MindmapStudio, title: 'Sơ đồ tư duy', description: 'Hệ thống hoá kiến thức & sinh thẻ ghi nhớ cho học sinh', icon: <SparklesIcon className="h-6 w-6 text-white" />, accent: 'bg-purple-500' },
            { view: View.ScientificSimulationCreator, title: 'Mô phỏng khoa học AI', description: 'Tạo công cụ học tập tương tác sinh động', icon: <BoltIcon className="h-6 w-6 text-white" />, accent: 'bg-teal-500' },
            { view: View.ThietKe3DCreator, title: 'Thiết kế ứng dụng 3D', description: 'Tạo mô hình 3D tương tác học tập', icon: <Square2StackIcon className="h-6 w-6 text-white" />, accent: 'bg-indigo-500' },
            { view: View.HomeworkCreator, title: 'Giao bài tập về nhà', description: 'Soạn và giao bài tập đa dạng cho học sinh', icon: <ClipboardDocumentListIcon className="h-6 w-6 text-white" />, accent: 'bg-amber-500' },
            { view: View.OralQuizCreator, title: 'Kiểm tra kiến thức', description: 'Tạo nội dung và tổ chức kiểm tra linh hoạt đầu, giữa hoặc cuối giờ', icon: <SparklesIcon className="h-6 w-6 text-white" />, accent: 'bg-indigo-500' },
        ],
    },
    {
        label: 'Sáng tạo & Tương tác',
        icon: <SparklesIcon className="h-5 w-5" />,
        tools: [
            { view: View.GameCreator, title: 'Tạo Trò chơi', description: 'Thiết kế trò chơi ôn tập tương tác', icon: <BoltIcon className="h-6 w-6 text-white" />, accent: 'bg-orange-500' },
            { view: View.ClassroomToolbox, title: 'Hộp công cụ lớp học', description: 'Vòng quay tên, đếm ngược, chia nhóm, xúc xắc, đo ồn — chiếu lên lớp', icon: <PuzzlePieceIcon className="h-6 w-6 text-white" />, accent: 'bg-cyan-500' },
            { view: View.InteractiveVideo, title: 'Video tương tác', description: 'Lồng ghép câu hỏi và tương tác vào video bài giảng', icon: <PlayIcon className="h-6 w-6 text-white" />, accent: 'bg-rose-600' },
            { view: View.VideoCreator, title: 'Tạo Video bài giảng', description: 'Chuyển nội dung thành video với MC ảo', icon: <PlayIcon className="h-6 w-6 text-white" />, accent: 'bg-rose-500' },
            { view: View.DesignCenter, title: 'Thiết kế Thông minh', description: 'Poster, thẻ từ vựng, bìa bài giảng với AI', icon: <SparklesIcon className="h-6 w-6 text-white" />, accent: 'bg-indigo-500' },
            { view: View.ActivityCreator, title: 'Hoạt động Sáng tạo', description: 'Kịch bản, bài hát, câu chuyện từ bài học', icon: <PuzzlePieceIcon className="h-6 w-6 text-white" />, accent: 'bg-indigo-600' },
            { view: View.SKKNCreator, title: 'Sáng kiến Kinh nghiệm', description: 'Viết và trình bày SKKN chuyên nghiệp', icon: <DocumentTextIcon className="h-6 w-6 text-white" />, accent: 'bg-emerald-600' },
        ],
    },
    {
        label: 'Định hướng & Hành trình khám phá',
        icon: <FlagIcon className="h-5 w-5" />,
        tools: [
            {
                view: View.CareerJourney,
                title: 'Hành trình khám phá nghề',
                description: 'Tạo hồ sơ mẫu cho học sinh, demo lộ trình nghề nghiệp - giáo viên dùng vai trò Phụ huynh để soạn dữ liệu minh hoạ.',
                icon: <FlagIcon className="h-6 w-6 text-white" />,
                accent: 'bg-violet-600'
            },
        ],
    },
];

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ onNavigate, user, isLoading }) => {
    if (isLoading && !user) return <LoadingDataIndicator />;
    const toolGroups = TOOL_GROUPS.map(group => ({
        ...group,
        tools: group.tools.filter(tool => {
            if (tool.view === View.VideoCreator) return FEATURE_FLAGS.VIDEO_GENERATION !== false;
            if (tool.view === View.DesignCenter) return FEATURE_FLAGS.IMAGE_GENERATION !== false;
            return true;
        })
    })).filter(group => group.tools.length > 0);

    return (
        <div className="space-y-8">
            <HelpOnboarding steps={teacherHelpSteps} role="teacher" />

            {/* Hero Welcome */}
            <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-700 to-indigo-800 text-white shadow-xl">
                <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-48 h-48 bg-teal-400/20 rounded-full blur-2xl" />
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
                <div className="relative p-6 sm:p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                            <AcademicCapIcon className="h-8 w-8" />
                        </div>
                        <div>
                            <p className="text-teal-200 text-sm font-medium uppercase tracking-wider">Bảng điều khiển</p>
                            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                                {getDashboardGreeting('teacher', user.teacherName || user.email || '', user.greeting)}
                                <button 
                                    onClick={() => onNavigate(View.AffiliateDashboard)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-xs font-black uppercase tracking-widest transition-all border border-white/10 group/income active:scale-95"
                                    title="Tham gia chương trình Affiliate để tăng thu nhập cùng Bookai"
                                >
                                    <WalletIcon className="h-3.5 w-3.5 text-amber-300 group-hover/income:scale-110 transition-transform" />
                                    <span className="hidden sm:inline">Tăng thu nhập</span>
                                    <span className="sm:hidden">Affiliate</span>
                                </button>
                            </h1>
                        </div>
                    </div>
                    <p className="text-teal-100/90 max-w-xl text-sm md:text-base">
                        {getDashboardSubtitle('teacher')}
                    </p>
                </div>
            </section>

            {/* Tool Groups */}
            <div className="space-y-8">
                {toolGroups.map((group) => (
                    <section key={group.label}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-8 w-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
                                {group.icon}
                            </div>
                            <h2 className="text-lg font-bold text-slate-800">{group.label}</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {group.tools.map((tool) => (
                                <ToolCard
                                    key={tool.view}
                                    title={tool.title}
                                    description={tool.description}
                                    icon={tool.icon}
                                    accent={tool.accent}
                                    onClick={() => onNavigate(tool.view)}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};

export default TeacherDashboard;